import React, { useEffect, useState } from 'react';
import { Sidebar } from '@/components/Sidebar';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { api } from '@/utils/api';
import { toast } from 'sonner';
import {
  Brain, PlayCircle, RefreshCw, CheckCircle2, ShoppingCart,
  MessageSquare, Ban, Snowflake, Users, AlertCircle,
  Send, Reply, Flame, Phone, DollarSign, TrendingUp,
} from 'lucide-react';

function fmtDate(iso) {
  if (!iso) return '-';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('pt-BR') + ' ' + d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
}

const CATEGORY_META = {
  frio: { label: 'Frio', icon: Snowflake, color: 'bg-blue-100 text-blue-700 border-blue-200' },
  frio_failed: { label: 'Análise falhou', icon: AlertCircle, color: 'bg-slate-100 text-slate-700 border-slate-200' },
  comprou: { label: 'Já comprou', icon: ShoppingCart, color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  negociando: { label: 'Negociando', icon: MessageSquare, color: 'bg-amber-100 text-amber-700 border-amber-200' },
  pediu_parar: { label: 'Pediu parar', icon: Ban, color: 'bg-red-100 text-red-700 border-red-200' },
};

export default function ReengagementPage() {
  const [stats, setStats] = useState({ summary: {}, recent: [] });
  const [candidates, setCandidates] = useState({ total: 0, candidates: [] });
  const [funnel, setFunnel] = useState(null);
  const [funnelFilter, setFunnelFilter] = useState('all');
  const [funnelDays, setFunnelDays] = useState(7);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  const load = async () => {
    try {
      const [s, c, f] = await Promise.all([
        api.getReengagementStats(funnelDays),
        api.getReengagementCandidates(),
        api.getReengagementFunnel(funnelDays, funnelFilter),
      ]);
      setStats(s.data);
      setCandidates(c.data);
      setFunnel(f.data);
    } catch {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const i = setInterval(load, 60000);
    return () => clearInterval(i);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [funnelDays, funnelFilter]);

  const runNow = async () => {
    if (!window.confirm(`Disparar ciclo agora? Vai processar até 50 leads e enviar mensagem aos classificados como "frio".`)) return;
    setRunning(true);
    try {
      await api.runReengagementNow();
      toast.success('Ciclo iniciado em background. Atualizando dados em ~30s...');
      setTimeout(load, 30000);
    } catch {
      toast.error('Erro ao disparar ciclo');
    } finally {
      setRunning(false);
    }
  };

  if (loading) {
    return (
      <div className="flex h-screen">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600" />
        </div>
      </div>
    );
  }

  const s = stats.summary || {};

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/30">
      <Sidebar />
      <main className="flex-1 p-8" data-testid="reengagement-page">
        <div className="max-w-7xl mx-auto">
          <div className="mb-6 flex items-start justify-between">
            <div>
              <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                <Brain className="inline w-7 h-7 mr-2 text-emerald-600" />
                Reengajamento Inteligente
              </h1>
              <p className="text-slate-600 mt-1">
                IA analisa conversas e reativa leads parados em <code className="text-xs bg-slate-100 px-1.5 py-0.5 rounded">sem_contato</code> ou <code className="text-xs bg-slate-100 px-1.5 py-0.5 rounded">nao_interesse</code> sem interação há +48h.
              </p>
            </div>
            <div className="flex gap-2">
              <Button onClick={load} variant="outline" data-testid="btn-reload">
                <RefreshCw className="w-4 h-4 mr-2" /> Atualizar
              </Button>
              <Button onClick={runNow} disabled={running} className="bg-emerald-600 hover:bg-emerald-700" data-testid="btn-run-now">
                <PlayCircle className={`w-4 h-4 mr-2 ${running ? 'animate-pulse' : ''}`} />
                Disparar ciclo agora
              </Button>
            </div>
          </div>

          {/* Alerta de saldo OpenRouter */}
          {(stats.summary?.analise_falhou || 0) > 5 && (
            <Card className="mb-6 p-4 border-l-4 border-l-red-500 bg-red-50">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
                <div className="flex-1">
                  <p className="font-semibold text-red-900">Atenção: {stats.summary.analise_falhou} análises falharam</p>
                  <p className="text-sm text-red-700 mt-1">
                    A IA não consegue analisar leads — provavelmente o saldo do OpenRouter está zerado em produção.
                    Acesse <a href="https://openrouter.ai/credits" target="_blank" rel="noopener noreferrer" className="underline font-medium">openrouter.ai/credits</a> e adicione saldo na chave de produção.
                  </p>
                </div>
              </div>
            </Card>
          )}

          {/* CAP DIARIO */}
          {stats.daily_cap && (
            <Card className={`mb-6 p-5 border-l-4 ${stats.daily_cap.blocked ? 'border-l-red-500 bg-red-50' : stats.daily_cap.percentage > 75 ? 'border-l-amber-500 bg-amber-50' : 'border-l-emerald-500'}`}>
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="font-semibold text-slate-800 flex items-center gap-2">
                    🛡️ Cap diário (anti-ban WhatsApp)
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    {stats.daily_cap.blocked
                      ? '⛔ Limite atingido! IA pausada até amanhã às 9h.'
                      : `Hoje a IA pode enviar mais ${stats.daily_cap.remaining} mensagens antes de parar.`}
                  </p>
                </div>
                <div className="text-right">
                  <p className={`text-3xl font-bold ${stats.daily_cap.blocked ? 'text-red-600' : stats.daily_cap.percentage > 75 ? 'text-amber-600' : 'text-emerald-600'}`}>
                    {stats.daily_cap.sent_today}/{stats.daily_cap.limit}
                  </p>
                  <div className="w-32 h-2 bg-slate-200 rounded-full mt-1 overflow-hidden">
                    <div
                      className={`h-full rounded-full ${stats.daily_cap.blocked ? 'bg-red-500' : stats.daily_cap.percentage > 75 ? 'bg-amber-500' : 'bg-emerald-500'}`}
                      style={{ width: `${Math.min(100, stats.daily_cap.percentage)}%` }}
                    />
                  </div>
                </div>
              </div>
            </Card>
          )}

          {/* Resumo */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
            <Card className="p-4 border-l-4 border-l-slate-400">
              <p className="text-xs text-slate-500">Candidatos elegíveis</p>
              <p className="text-3xl font-bold text-slate-900 mt-1">{candidates.total}</p>
              <p className="text-xs text-slate-400 mt-1">+48h sem interação</p>
            </Card>
            <Card className="p-4 border-l-4 border-l-blue-500">
              <p className="text-xs text-slate-500">Reengajados ({funnelDays}d)</p>
              <p className="text-3xl font-bold text-blue-600 mt-1">{s.frio_sent || 0}</p>
              <p className="text-xs text-slate-400 mt-1">Mensagens enviadas</p>
            </Card>
            <Card className="p-4 border-l-4 border-l-emerald-500">
              <p className="text-xs text-slate-500">Já compraram ({funnelDays}d)</p>
              <p className="text-3xl font-bold text-emerald-600 mt-1">{s.comprou || 0}</p>
              <p className="text-xs text-slate-400 mt-1">Atualize status</p>
            </Card>
            <Card className="p-4 border-l-4 border-l-amber-500">
              <p className="text-xs text-slate-500">Negociando ({funnelDays}d)</p>
              <p className="text-3xl font-bold text-amber-600 mt-1">{s.negociando || 0}</p>
              <p className="text-xs text-slate-400 mt-1">Operador deve cuidar</p>
            </Card>
            <Card className="p-4 border-l-4 border-l-red-500">
              <p className="text-xs text-slate-500">Pediram parar ({funnelDays}d)</p>
              <p className="text-3xl font-bold text-red-600 mt-1">{s.pediu_parar || 0}</p>
              <p className="text-xs text-slate-400 mt-1">IA pausada permanente</p>
            </Card>
          </div>

          {/* === FUNIL DE CONVERSAO === */}
          {funnel && (
            <Card className="mb-6 p-5">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="font-semibold text-slate-800 flex items-center">
                    <TrendingUp className="w-5 h-5 mr-2 text-emerald-600" />
                    Funil de Conversão da IA
                  </h2>
                  <p className="text-xs text-slate-500 mt-1">Está a IA ajudando a vender de verdade?</p>
                </div>
                <div className="flex gap-2 items-center">
                  <label className="text-xs text-slate-500">Período:</label>
                  <select
                    value={funnelDays}
                    onChange={(e) => setFunnelDays(Number(e.target.value))}
                    className="text-sm border rounded px-2 py-1"
                    data-testid="funnel-period-select"
                  >
                    <option value={7}>7 dias</option>
                    <option value={30}>30 dias</option>
                    <option value={90}>90 dias</option>
                  </select>
                </div>
              </div>

              {/* Stages */}
              <div className="grid grid-cols-5 gap-2 mb-4">
                {[
                  { key: 'sent', label: 'Enviados', value: funnel.funnel?.sent, icon: Send, color: 'blue' },
                  { key: 'responded', label: 'Responderam', value: funnel.funnel?.responded, icon: Reply, color: 'cyan' },
                  { key: 'hot', label: 'Aceitaram ligar', value: funnel.funnel?.hot, icon: Flame, color: 'orange' },
                  { key: 'called', label: 'Operador ligou', value: funnel.funnel?.called, icon: Phone, color: 'amber' },
                  { key: 'sold', label: 'Vendas', value: funnel.funnel?.sold, icon: DollarSign, color: 'emerald' },
                ].map((stage) => {
                  const Icon = stage.icon;
                  return (
                    <div key={stage.key} className={`p-3 rounded-lg bg-${stage.color}-50 border border-${stage.color}-200`}>
                      <div className="flex items-center gap-1.5 mb-1">
                        <Icon className={`w-3.5 h-3.5 text-${stage.color}-600`} />
                        <p className="text-[10px] text-slate-600 uppercase font-medium">{stage.label}</p>
                      </div>
                      <p className={`text-2xl font-bold text-${stage.color}-700`}>{stage.value || 0}</p>
                    </div>
                  );
                })}
              </div>

              {/* Rates */}
              <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs border-t pt-3">
                <span><strong className="text-slate-700">Taxa de resposta:</strong> <span className="text-cyan-700 font-semibold">{funnel.rates?.response_rate || 0}%</span></span>
                <span><strong className="text-slate-700">Taxa de aquecimento:</strong> <span className="text-orange-700 font-semibold">{funnel.rates?.hot_rate || 0}%</span></span>
                <span><strong className="text-slate-700">Taxa de conversão final:</strong> <span className="text-emerald-700 font-semibold">{funnel.rates?.sold_rate || 0}%</span></span>
                {funnel.total_revenue_from_reengagement > 0 && (
                  <span className="ml-auto"><strong className="text-emerald-700">Receita gerada:</strong> <span className="text-emerald-700 font-bold">R$ {funnel.total_revenue_from_reengagement.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span></span>
                )}
              </div>
            </Card>
          )}

          {/* Detalhamento leads reengajados */}
          {funnel && (
            <Card className="mb-6">
              <div className="p-4 border-b flex items-center justify-between flex-wrap gap-2">
                <div>
                  <h2 className="font-semibold text-slate-800 flex items-center">
                    <Users className="w-5 h-5 mr-2 text-emerald-600" />
                    Leads reengajados — detalhamento
                  </h2>
                  <p className="text-xs text-slate-500 mt-1">{funnel.details?.length || 0} leads encontrados no filtro</p>
                </div>
                <select
                  value={funnelFilter}
                  onChange={(e) => setFunnelFilter(e.target.value)}
                  className="text-sm border rounded px-3 py-1.5"
                  data-testid="funnel-filter-select"
                >
                  <option value="all">Todos</option>
                  <option value="responded">Só respondeu</option>
                  <option value="hot">Só HOT (aceitou ligar)</option>
                  <option value="sold">Só virou venda</option>
                </select>
              </div>
              {(!funnel.details || funnel.details.length === 0) ? (
                <div className="p-8 text-center text-slate-400">Nenhum lead no filtro selecionado</div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Lead</TableHead>
                        <TableHead>Operador</TableHead>
                        <TableHead>Reengajado em</TableHead>
                        <TableHead>Respondeu?</TableHead>
                        <TableHead>Aceitou ligar?</TableHead>
                        <TableHead title='Marcado quando o operador clica em "Marcar como ligado" no detalhe do lead'>Operador ligou? ℹ️</TableHead>
                        <TableHead>Status atual</TableHead>
                        <TableHead>Virou venda?</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {funnel.details.map((d) => (
                        <TableRow key={d.lead_id}>
                          <TableCell>
                            <div className="font-medium text-sm">{d.lead_name}</div>
                            <div className="text-xs text-slate-400 font-mono">{d.lead_phone}</div>
                          </TableCell>
                          <TableCell className="text-xs">{d.operator_name || '—'}</TableCell>
                          <TableCell className="text-xs text-slate-600">{fmtDate(d.reengaged_at)}</TableCell>
                          <TableCell>
                            {d.responded ? (
                              <Badge className="bg-cyan-100 text-cyan-700 border-cyan-200 border text-xs">
                                <Reply className="w-3 h-3 mr-1" /> Sim
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="text-xs text-slate-400">Não</Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            {d.accepted_call ? (
                              <Badge className="bg-orange-100 text-orange-700 border-orange-200 border text-xs">
                                <Flame className="w-3 h-3 mr-1" /> HOT
                              </Badge>
                            ) : d.response_class === 'rejected' ? (
                              <Badge className="bg-red-50 text-red-600 border-red-200 border text-xs">Recusou</Badge>
                            ) : d.responded ? (
                              <Badge variant="outline" className="text-xs">Ambíguo</Badge>
                            ) : (
                              <span className="text-xs text-slate-300">—</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {d.called_by_operator ? (
                              <Badge className="bg-amber-100 text-amber-700 border-amber-200 border text-xs">
                                <Phone className="w-3 h-3 mr-1" /> Sim
                              </Badge>
                            ) : (
                              <span className="text-xs text-slate-300">—</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="text-[10px]">{d.lead_status_now}</Badge>
                          </TableCell>
                          <TableCell>
                            {d.converted_to_sale ? (
                              <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 border text-xs">
                                <DollarSign className="w-3 h-3 mr-1" /> R$ {(d.sale_amount || 0).toLocaleString('pt-BR', { minimumFractionDigits: 0 })}
                              </Badge>
                            ) : (
                              <span className="text-xs text-slate-300">—</span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </Card>
          )}

          {/* Decisões recentes */}
          <Card className="mb-6">
            <div className="p-4 border-b">
              <h2 className="font-semibold text-slate-800 flex items-center">
                <Brain className="w-5 h-5 mr-2 text-emerald-600" />
                Decisões recentes da IA
              </h2>
              <p className="text-xs text-slate-500 mt-1">Últimas {stats.recent?.length || 0} análises de leads</p>
            </div>
            {!stats.recent || stats.recent.length === 0 ? (
              <div className="p-8 text-center text-slate-400">
                <AlertCircle className="w-10 h-10 mx-auto mb-2 opacity-40" />
                Nenhuma análise ainda. Clique em &quot;Disparar ciclo agora&quot; pra começar.
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Data</TableHead>
                    <TableHead>Lead</TableHead>
                    <TableHead>Status CRM</TableHead>
                    <TableHead>Classificação IA</TableHead>
                    <TableHead>Razão</TableHead>
                    <TableHead>Ação</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {stats.recent.map((r) => {
                    const meta = CATEGORY_META[r.category] || CATEGORY_META.frio;
                    const Icon = meta.icon;
                    return (
                      <TableRow key={r.id}>
                        <TableCell className="text-xs">{fmtDate(r.created_at)}</TableCell>
                        <TableCell className="font-medium">{r.lead_name}<div className="text-xs text-slate-400">{r.lead_phone}</div></TableCell>
                        <TableCell><Badge variant="outline" className="text-[10px]">{r.lead_status}</Badge></TableCell>
                        <TableCell>
                          <Badge className={`${meta.color} border text-xs`}>
                            <Icon className="w-3 h-3 mr-1" /> {meta.label}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs text-slate-600 max-w-md truncate">{r.reason}</TableCell>
                        <TableCell>
                          {r.sent ? (
                            <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 border text-xs">
                              <CheckCircle2 className="w-3 h-3 mr-1" /> Enviado
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-xs">Não enviado</Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </Card>

          {/* Candidatos elegíveis */}
          <Card>
            <div className="p-4 border-b">
              <h2 className="font-semibold text-slate-800 flex items-center">
                <Users className="w-5 h-5 mr-2 text-emerald-600" />
                Próximos candidatos
              </h2>
              <p className="text-xs text-slate-500 mt-1">
                {candidates.total} leads elegíveis no próximo ciclo (mostrando até 50)
              </p>
            </div>
            {candidates.candidates?.length === 0 ? (
              <div className="p-8 text-center text-slate-400">Nenhum lead elegível agora</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nome</TableHead>
                    <TableHead>Telefone</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Tentativas</TableHead>
                    <TableHead>Última msg</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {candidates.candidates?.map((c) => (
                    <TableRow key={c.id}>
                      <TableCell className="font-medium">{c.name}</TableCell>
                      <TableCell className="text-xs font-mono">{c.phone}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-[10px]">{c.status}</Badge>
                      </TableCell>
                      <TableCell>
                        <span className="text-xs">{c.reengagement_count || 0}/3</span>
                      </TableCell>
                      <TableCell className="text-xs text-slate-500">
                        {c.last_message_at ? fmtDate(c.last_message_at) : 'Nunca'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Card>
        </div>
      </main>
    </div>
  );
}
